export default class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();

    this.detectedCountry = this.dataset.market;
    this.hasDeterminedCountry = false;
  }

  static observedAttributes = ["data-cart-total"];

  async connectedCallback() {
    // Only use geolocation if customer is on UK/ROW market site, since we need country to know shipping thresholds.
    // Otherwise, assume that customer wants to ship to the market they are in (for US/CA).
    if (this.dataset.market === "GB") {
      const browsingContext = await fetch(
        window.Shopify.routes.root +
          "browsing_context_suggestions.json" +
          "?country[enabled]=true" +
          `&country[exclude]=${window.Shopify.country}`,
      )
        .then((response) => response.json())
        .catch((error) => {
          console.error(error);
        });

      const detectedCountryHandle =
        browsingContext?.detected_values?.country?.handle;

      // set `this.detectedCountry` if geolocation detected, unless location is US or CA
      // for US/CA location, assume customer has chosen to be on UK/ROW market site rather than site for their market
      if (
        detectedCountryHandle &&
        detectedCountryHandle !== "US" &&
        detectedCountryHandle !== "CA"
      ) {
        this.detectedCountry = detectedCountryHandle;
      }
    }

    this.hasDeterminedCountry = true;
    this.updateProgress();
  }

  attributeChangedCallback() {
    // Only run once client-side geolocation has run,
    // since 1st change of `data-cart-total` is technically during HTML parsing
    // and before geolocation has had a chance to run
    if (this.hasDeterminedCountry) {
      this.updateProgress();
    }
  }

  updateProgress() {
    const threshold = this.getThresholdForCountry(this.detectedCountry);
    const cartTotal = this.dataset.cartTotal;
    const progress = Number(cartTotal) || 0;

    if (threshold && cartTotal) {
      this.updateProgressBar(threshold, progress);
      this.updateProgressText(threshold, progress);
    }

    if (!threshold) {
      this.setAttribute("inert", "");
    } else {
      this.removeAttribute("inert");
    }
  }

  updateProgressBar(threshold, progress) {
    const progressEl = this.querySelector("progress");

    if (!progressEl) return;

    progressEl.setAttribute("max", threshold);
    progressEl.setAttribute("value", Math.min(progress, threshold));
  }

  updateProgressText(threshold, progress) {
    const progressTextEl = this.querySelector("[data-progress-text]");

    if (!progressTextEl) return;

    const difference = threshold - progress;
    const differenceInMoney = this.formatCurrency(difference / 100);
    const shippingType = this.getShippingTypeForCountry();

    let innerHTML;

    if (difference > 0) {
      innerHTML = `Add <strong>${differenceInMoney}</strong> more for <strong>Free ${shippingType} Shipping</strong>`;
    } else {
      innerHTML = `Your cart qualifies for <strong>Free ${shippingType} Shipping</strong>!`;
    }

    progressTextEl.innerHTML = innerHTML;
  }

  getThresholdForCountry(countryCode) {
    switch (countryCode) {
      case "US":
        return 4000;
      case "CA":
        return 10000;
      case "GB":
        return 3000;
      // EU countries
      case "AT":
      case "BE":
      case "BG":
      case "HR":
      case "DK":
      case "EE":
      case "FR":
      case "GR":
      case "HU":
      case "IE":
      case "IT":
      case "LV":
      case "LT":
      case "MT":
      case "NL":
      case "NO":
      case "PL":
      case "PT":
      case "RO":
      case "SK":
      case "SI":
      case "ES":
        return 15000;
      default:
        return null;
    }
  }

  getCurrencyForCountry() {
    switch (this.detectedCountry) {
      case "US":
        return "USD";
      case "CA":
        return "CAD";
      default:
        return "GBP";
    }
  }

  getShippingTypeForCountry() {
    switch (this.detectedCountry) {
      case "US":
        return "US";
      case "CA":
        return "Canadian";
      default:
        return "UK";
    }
  }

  formatCurrency(number) {
    const currency = this.getCurrencyForCountry();

    const formatter = new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    });

    return formatter.format(number);
  }
}
